import { FC, useEffect, useState } from 'react';
import { z, ZodErrorMap, ZodIssueCode } from "zod";

import {
  Select, FormControlLabel, Radio, RadioGroup, MenuItem,
  FormControl, InputLabel, Grid, Typography, DialogActions,
  DialogContent, FormHelperText, TextField, Divider,
  styled,
  CircularProgress,
  Box
} from '@mui/material';
import Banner from "@/components/Banner/Banner";
import { useTranslation } from 'react-i18next';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import { useContracts } from '@/components/Contracts/hooks/useContracts';
import { ContractDisplayTypes } from '@/components/Contracts/types/contracts';
import { Controller, FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { ChangeAddressData, UseAddressType } from '@/components/Contracts/types/address';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@gisce/oficina-virtual-components';
import { validarReferenciaCatastral } from '@/components/form-fields/AddressForm/AddressField';
import { Address } from '@/components/form-fields/AddressForm/types/address';
import AddressForm from '@/components/form-fields/AddressForm/AddressForm';
import { useAddress } from '@/components/Contracts/hooks/useAddress';
import { LoadingAnimation } from '@/components/LoadingAnimation';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input'
import * as EmailValidator from 'email-validator';
import { default as contractActions } from "@/components/Contracts/actions/newActions"
import { useAppDispatch } from '@/hooks';

interface ChangeAddressProps {
  type: ContractDisplayTypes;
  onClose: () => void;
}

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
  .MuiTelInput-IconButton {
    display: none;
  }
`;

const ChangeAddress: FC<ChangeAddressProps> = ({
  type,
  onClose
}) => {
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const { currentContract } = useContracts(type);
  const { availableAddresses, state, createAddress, updateAddress } = useAddress(type);

  const customErrorMap: ZodErrorMap = (issue, ctx) => {
    if (issue.code === ZodIssueCode.invalid_enum_value) {
      return { message: t('common:text.required_field') };
    }
    if (issue.code === ZodIssueCode.invalid_type) {
      return { message: t('common:text.required_field') };
    }

    // Retorna el missatge per defecte per altres errors
    return { message: ctx.defaultError };
  };

  const schema = z.discriminatedUnion("alreadyRegistered", [
    z.object({
      alreadyRegistered: z.literal('existing'),
      addressId: z.number()
    }),
    z.object({
      alreadyRegistered: z.literal('new'),
      address: z.object({
        name: z.string().min(1, t('common:text.required_field')).max(256, 
          t('common:text.address_validation_nv_length')),
        dp: z.string().min(1, t('common:text.required_field'))
          .min(5, t('common:text.contractation_postal_code_length')),
        ine: z.string().min(1, t('common:text.required_field')),
        state: z.number({errorMap: customErrorMap}).min(1, t('common:text.required_field')),
        poblacio: z.string({errorMap: customErrorMap}).min(1, t('common:text.required_field')),
        tv: z.number({errorMap: customErrorMap}).min(1, t('common:text.required_field')),
        nv: z.string().min(1, t('common:text.required_field')).max(256, 
          t('common:text.address_validation_nv_length')),
        pnp: z.string().min(1, t('common:text.required_field')).max(9, 
          t('common:text.address_validation_pnp_length')),
        bq: z.string().max(4, t('common:text.address_validation_bq_length')).optional(),
        es: z.string().max(4, t('common:text.address_validation_es_length')).optional(),
        pt: z.string().max(10, t('common:text.address_validation_pt_length')).optional(),
        pu: z.string().max(4, t('common:text.address_validation_pu_length')).optional(),
        ref_catastral: z.string()
          .refine(
            val => validarReferenciaCatastral(val), 
            t('common:text.contractation_not_valid_ref')
          ).optional(),
        aclarador: z.string().optional(),
        mobile: z.string()
          .min(4, t('common:text.required_field'))
          .refine(
            (tel) => matchIsValidTel(tel), 
            {message: t('common:text.lead_contact_phone_not_valid')}
          ).optional(),
        email: z.string()
          .refine(
            (email) => EmailValidator.validate(email)
          ).optional()
      }) satisfies z.ZodType<Address>,
    })
  ]) satisfies z.ZodType<ChangeAddressData>;

  const methods = useForm<ChangeAddressData>({
    resolver: zodResolver(schema),
    defaultValues: {
      alreadyRegistered: "new",
      address: {
        name: currentContract?.fiscal_partner.name,
        state: null,
        ine: '',
        poblacio: '',
        tv: null, // Street Type (Tipo Via)
        nv: '', // Street Name (Nombre Via)
        pnp: '', // Street Number
        bq: '', // Block (Bloque)
        es: '', // Stair (Escalera)
        pt: '', // Floor
        pu: '', // Door (Puerta)
        dp: '', // Dirección Postal (Zip)
        aclarador: '',
        ref_catastral: '',
        email: '',
        mobile: '',
      }
    }
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const watchedAlreadyRegistered = watch("alreadyRegistered");
  
  useEffect(() => {
    if (state === "success") {
      dispatch(contractActions[type].notificationFormClosed());
      handleClose();
    }
  }, [state]);

  const onChangeAddressSubmit = (formValues: ChangeAddressData) => {
    if (formValues.alreadyRegistered === 'existing') {
      if (currentContract){
        updateAddress(formValues.addressId, currentContract.name);
      }
    }
    else {
      if (currentContract){
        createAddress(formValues.address, currentContract.name);
      }
    }
  }

  const handleClose = () => {
    onClose();
  };

  const addressChoices = availableAddresses.map((address) => {
    const label = <div style={{ marginTop: '2vh' }}>
      {`${address.name}`}
      <br/>
      {`${address.full_address}`}
      <br/>
      {`${address.email}`}
      <br/>
      {`${address.mobile}`}
    </div>;
    return <FormControlLabel key={address.id} value={address.id} control={<Radio />} label={label} />;
  });

  const renderInitForm = () => {
    return <>
      <FormProvider {...methods}>
        <form onSubmit={
            handleSubmit(onChangeAddressSubmit)
        }>

          <DialogContent>

            <Controller
              name="alreadyRegistered"
              control={control}
              render={({ field }) => (
                <FormControl style={{ width: "100%" }}>
                  <InputLabel shrink>
                    {t('common:text.change_address_type')}
                  </InputLabel>
                  <Select
                    {...field}
                  >
                    <MenuItem key="existing" value="existing">{t('common:text.change_address_existing')}</MenuItem>
                    <MenuItem key="new" value="new">{t('common:text.change_address_new')}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            <Divider style={{marginTop: 20}}/>

            <>
            {watchedAlreadyRegistered === 'new' && <>
              {
                <Typography variant="subtitle1" style={{marginTop: 20}}>
                  {t('common:text.contractation_contact_details')}
                </Typography>
              }
              <Grid container spacing={3}>
                {
                  <Grid item xs={12} sm={6} >
                    <Controller
                      name="address.mobile"
                      control={control}
                      render={({ field, fieldState }) => (<>
                        <NoFlagMuiTelInputStyled
                          {...field}
                          label={t('common:text.address_phone')}
                          style={{ width: "100%" }}
                          error={!!(fieldState.error)}
                          disableDropdown={true}
                          // forceCallingCode={true}
                          defaultCountry="ES"
                          helperText={fieldState.error?.message}
                          fullWidth={true}
                        />
                      </>)}
                    />
                  </Grid>
                }
                {
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="address.email"
                      control={control}
                      render={({ field, fieldState }) => (<>
                        <TextField
                          {...field}
                          label={t('common:text.address_email')}
                          style={{ width: "100%" }}
                          error={!!(fieldState.error)}
                        />
                        {fieldState.error && (
                          <FormHelperText error={true}>
                            {fieldState.error.message}
                          </FormHelperText>
                        )}
                      </>)}
                    />
                  </Grid>
                }
                {
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="address.name"
                      control={control}
                      render={({ field, fieldState }) => (<>
                        <TextField
                          {...field}
                          label={t('common:text.address_email')}
                          style={{ width: "100%" }}
                          error={!!(fieldState.error)}
                        />
                        {fieldState.error && (
                          <FormHelperText error={true}>
                            {fieldState.error.message}
                          </FormHelperText>
                        )}
                      </>)}
                    />
                  </Grid>
                }
              </Grid>

              <AddressForm/>
            </>}

            {watchedAlreadyRegistered === 'existing' &&
              <div>
                {!(state === "fetching") ?
                  availableAddresses.length ?
                    <>
                      <Controller
                        name="addressId"
                        control={control}
                        render={({ field }) =>(
                          <RadioGroup
                            {...field}
                            value={field.value ?? currentContract?.notification_address.id ?? null }
                            onChange={event => field.onChange(Number(event.target.value))}
                          >
                            {addressChoices}
                          </RadioGroup>
                        )}
                      />
                    </>
                  :
                    <p style={{ padding: '1vh' }}>
                      {t('common:text.change_address_no_data')}
                    </p>
                  :
                  <LoadingAnimation />
                }
              </div>
            }
            </>

          </DialogContent>

          <DialogActions sx={{display: "flex", justifyContent: "flex-start"}}>
            <Button
              variant={'outlined'}
              color={"primary"}
              onClick={handleClose}
            >
              {t('common:text.power_modification_close')}
            </Button>
            <Button
              type="submit"
              variant={'contained'}
              color={'primary'}
              disabled={isSubmitting || state == "submitting"}
            >
              {isSubmitting || state == "submitting" ? <CircularProgress size={25} /> : null}
              {t('common:text.iban_change_update')}
            </Button>
          </DialogActions>

        </form>
      </FormProvider>
    </>
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="stretch"
    >
      <Grid item xs={12} style={{ padding: 12 }}>
        <Typography variant="h5">{t('common:text.change_address_edit')}</Typography>
        {renderInitForm()}
      </Grid>
      <Grid item xs={6} style={{ padding: 12 }}>
        <Banner code='canvi_change_address_ov' />
      </Grid>
    </Grid>
  );
};

export default ChangeAddress;
